import React, { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import "./productcard.css";
import { Rate } from 'antd';
import QuickViewModal from './QuickViewModal';
import * as newApi from "../../api/apiCollection"
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
// icons imports
import { FaMinus, FaPlus, FaShoppingCart } from 'react-icons/fa';
import { StarFilled, StarOutlined } from '@ant-design/icons';
import { IoMdArrowDropdown } from "react-icons/io";
// Reducer imports
import { addGuestCartTotal, addtoGuestCart, setCart, setCartProducts, setCartSubTotal, subGuestCartTotal } from '../../model/reducer/cartReducer';
import { useTranslation } from 'react-i18next';
import ProductVariantModal from './ProductVariantModal';
import ImageWithPlaceholder from '../image-with-placeholder/ImageWithPlaceholder';
import { BiHeart, BiSolidHeart } from 'react-icons/bi';
import { LuEye } from "react-icons/lu";
import { addFavoriteProductId } from '../../model/reducer/favouriteReducer';
import VegIcon from "../../utils/Icons/VegIcon.svg";
import NonVegIcon from "../../utils/Icons/NonVegIcon.svg";

const ProductCard = ({ product }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch()
    const { t } = useTranslation();
    
    // Reducer imports
    const setting = useSelector(state => (state.setting));
    const cart = useSelector(state => (state.cart))
    const user = useSelector(state => (state.user))
    const favoriteProducts = useSelector(state => (state.favourite))

    // State variables
    const [selectedVariant, setSelectedVariant] = useState()
    const [selectedColor, setSelectedColor] = useState(null);
    const [selectedSize, setSelectedSize] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [showVariantModal, setShowVariantModal] = useState(false)
    const [discountCountdown, setDiscountCountdown] = useState(null);

    // Helper functions for parsing variant data
    const getVariantColors = (variant) => {
        try {
            if (!variant?.colors) return [];
            const colors = JSON.parse(variant.colors);
            return Array.isArray(colors) ? colors : [];
        } catch (error) {
            console.error('Error parsing colors:', error);
            return [];
        }
    };

    const getVariantSizes = (variant) => {
        try {
            if (!variant?.size) return [];
            const sizes = JSON.parse(variant.size);
            return Array.isArray(sizes) ? sizes : [];
        } catch (error) {
            console.error('Error parsing sizes:', error);
            return [];
        }
    };

    const isClothingProduct = (variant) => {
        return variant?.variant_type === 'clothes';
    };

    const isStandardProduct = (variant) => {
        return variant?.variant_type === 'standard';
    };

    // Check for free quantity offers
    const hasFreeQuantityOffer = (variant) => {
        return variant?.free_quantity && variant?.free_quantity > 0;
    };

    const getFreeQuantityText = (variant) => {
        if (!hasFreeQuantityOffer(variant)) return null;
        return `${variant.free_quantity}`;
    };

    // Get variant display name
    const getVariantDisplayName = (variant) => {
        if (isClothingProduct(variant)) {
            const colors = getVariantColors(variant);
            const sizes = getVariantSizes(variant);
            let name = '';
            
            if (colors.length > 0) {
                name += colors[0];
            }
            if (sizes.length > 0) {
                name += name ? ` - ${sizes[0]}` : sizes[0];
            }
            
            return name || 'متغير';
        } else if (isStandardProduct(variant)) {
            return `${variant.measurement} ${variant.stock_unit_name || 'وحدة'}`;
        }
        
        return 'متغير';
    };

    // Initialize product variant and options
    useEffect(() => {
        const inStockVariant = product?.variants?.find((variant) => 
            variant?.is_unlimited_stock === 0 && variant?.stock > 0
        );
        const selected = inStockVariant ?? product?.variants[0];
        setSelectedVariant(selected);

        // Set default color and size if available
        if (selected && isClothingProduct(selected)) {
            const colors = getVariantColors(selected);
            const sizes = getVariantSizes(selected);
            
            if (colors.length > 0) {
                setSelectedColor(colors[0]);
            }
            
            if (sizes.length > 0) {
                setSelectedSize(sizes[0]);
            }
        }

        // Setup discount countdown
        if (selected?.discounted_price && selected?.discounted_price < selected?.price) {
            const now = new Date();
            const startDate = new Date(selected.discount_start_date);
            const endDate = new Date(selected.discount_end_date);

            if (now >= startDate && now <= endDate) {
                const interval = setInterval(() => {
                    const remaining = endDate - new Date();
                    if (remaining <= 0) {
                        clearInterval(interval);
                        setDiscountCountdown(null);
                    } else {
                        const days = Math.floor(remaining / (1000 * 60 * 60 * 24));
                        const hours = Math.floor((remaining / (1000 * 60 * 60)) % 24);
                        const minutes = Math.floor((remaining / (1000 * 60)) % 60);
                        const seconds = Math.floor((remaining / 1000) % 60);
                        setDiscountCountdown(`${days} يوم ${hours} ساعة ${minutes} دقيقة ${seconds} ثانية`);
                    }
                }, 1000);

                return () => clearInterval(interval);
            }
        }
    }, [product]);

    // Cart operations
    const addToCart = async (productId, productVId, qty) => {
        try {
            const response = await newApi.addToCart({ 
                product_id: productId, 
                product_variant_id: productVId, 
                qty: qty,
                selected_color: selectedColor,
                selected_size: selectedSize
            });
            
            if (response.status === 1) {
                const existingProduct = cart?.cartProducts?.find((product) => 
                    product?.product_id == productId && 
                    product?.product_variant_id == productVId &&
                    product?.selected_color === selectedColor &&
                    product?.selected_size === selectedSize
                );
                
                if (!existingProduct?.qty) {
                    // Add new product to cart
                    dispatch(setCart({ data: response }));
                    const updatedCartCount = [...cart?.cartProducts, { 
                        product_id: productId, 
                        product_variant_id: productVId, 
                        qty: qty,
                        selected_color: selectedColor,
                        selected_size: selectedSize 
                    }];
                    dispatch(setCartProducts({ data: updatedCartCount }));
                    dispatch(setCartSubTotal({ data: response?.sub_total }));
                } else {
                    // Update existing product quantity
                    const updatedProducts = cart?.cartProducts?.map(product => {
                        if (product.product_id == productId && product?.product_variant_id == productVId && product?.selected_color === selectedColor && product?.selected_size === selectedSize) {
                            return { 
                                ...product, 
                                qty: qty,
                                selected_color: selectedColor,
                                selected_size: selectedSize 
                            };
                        }
                        return product;
                    });
                    dispatch(setCart({ data: response }));
                    dispatch(setCartProducts({ data: updatedProducts }));
                    dispatch(setCartSubTotal({ data: response?.sub_total }));
                }
            }
        } catch (error) {
            console.log("error", error)
        }
    }

    const removeFromCart = async (productId, variantId) => {
        try {
            const response = await newApi.removeFromCart({ 
                product_id: productId, 
                product_variant_id: variantId,
                selected_color: selectedColor,
                selected_size: selectedSize
            })
            if (response?.status === 1) {
                const updatedProducts = cart?.cartProducts?.filter(product => {
                    // حذف المنتج المحدد فقط (نفس product_id, product_variant_id, selected_color, selected_size)
                    return !(product?.product_id === productId && 
                            product?.product_variant_id === variantId &&
                            product?.selected_color === selectedColor &&
                            product?.selected_size === selectedSize);
                });
                dispatch(setCartSubTotal({ data: response?.sub_total }));
                dispatch(setCartProducts({ data: updatedProducts }));
            } else {
                toast.error(response.message)
            }
        } catch (error) {
            console.log("error", error)
        }
    }

    // Guest cart operations
    const AddToGuestCart = (product, productId, productVariantId, Qty, isExisting, flag) => {
        const now = new Date();
        const startDate = selectedVariant?.discount_start_date ? new Date(selectedVariant.discount_start_date) : null;
        const endDate = selectedVariant?.discount_end_date ? new Date(selectedVariant.discount_end_date) : null;
        const isDiscountActive = selectedVariant?.discounted_price && 
                               selectedVariant?.discounted_price !== 0 && 
                               selectedVariant?.discounted_price < selectedVariant?.price &&
                               startDate && endDate &&
                               now >= startDate && now <= endDate;
        const finalPrice = isDiscountActive ? selectedVariant?.discounted_price : selectedVariant?.price
        
        if (isExisting) {
            let updatedProducts;
            if (Qty !== 0) {
                if (flag == "add") {
                    dispatch(addGuestCartTotal({ data: finalPrice }));
                } else if (flag == "remove") {
                    dispatch(subGuestCartTotal({ data: finalPrice }));
                }
                updatedProducts = cart?.guestCart?.map((product) => {
                    if (product?.product_id == productId && product?.product_variant_id == productVariantId) {
                        return { 
                            ...product, 
                            qty: Qty,
                            selected_color: selectedColor,
                            selected_size: selectedSize
                        };
                    }
                    return product;
                });
            } else {
                if (flag == "add") {
                    dispatch(addGuestCartTotal({ data: finalPrice }));
                } else if (flag == "remove") {
                    dispatch(subGuestCartTotal({ data: finalPrice }));
                }
                updatedProducts = cart?.guestCart?.filter(product =>
                    product?.product_id != productId && product?.product_variant_id != productVariantId
                );
            }
            dispatch(addtoGuestCart({ data: updatedProducts }));
        } else {
            if (flag == "add") {
                dispatch(addGuestCartTotal({ data: finalPrice }));
            } else if (flag == "remove") {
                dispatch(subGuestCartTotal({ data: finalPrice }));
            }
            const productData = { 
                product_id: productId, 
                product_variant_id: productVariantId, 
                qty: Qty,
                selected_color: selectedColor,
                selected_size: selectedSize
            };
            dispatch(addtoGuestCart({ data: [...cart?.guestCart, productData] }));
        }
    };

    // Utility functions
    const getProductQuantities = (products) => {
        if (!products || !Array.isArray(products) || products.length === 0) {
            return [];
        }
        const quantities = products.reduce((acc, product) => {
            if (!product || typeof product !== 'object') {
                return acc;
            }
            const productId = product.product_id;
            if (!productId) {
                return acc;
            }
            const existingQty = acc[productId] || 0;
            const qty = product.qty || 0;
            return { ...acc, [productId]: existingQty + qty };
        }, {});
        
        return Object.entries(quantities).map(([productId, qty]) => ({
            product_id: parseInt(productId),
            qty
        }));
    }

    const calculateDiscount = (discountPrice, actualPrice) => {
        const difference = actualPrice - discountPrice;
        const actualDiscountPrice = (difference / actualPrice)
        return actualDiscountPrice * 100;
    }

    // Favorite operations
    const addToFavorite = async (prdctId) => {
        try {
            if (user?.jwtToken) {
                const res = await newApi.addToFavorite({ product_id: prdctId });
                if (res.status == 1) {
                    toast.success(res.message)
                    dispatch(addFavoriteProductId({ data: prdctId }))
                } else {
                    toast.error(res.message)
                }
            } else {
                toast.error(t("required_login_message_for_wishlist"))
            }
        } catch (error) {
            console.log(error)
        }
    }

    // Cart validation and handling
    const handleValidateAddNewProduct = (productQuantity, product) => {
        const productQty = productQuantity?.find(prdct => prdct?.product_id == product?.id)?.qty

        if ((productQty || 0) >= Number(product?.total_allowed_quantity)) {
            toast.error('Oops, Limited Stock Available');
        } else if (Number(product.is_unlimited_stock)) {
            addToCart(product.id, selectedVariant.id, 1);
        } else {
            if (selectedVariant?.status) {
                addToCart(product.id, selectedVariant?.id, 1);
            } else {
                toast.error('Oops, Limited Stock Available');
            }
        }
    };

    const handleValidateAddExistingProduct = (productQuantity, product) => {
        const productQty = productQuantity?.find(prdct => prdct?.product_id == product?.id)?.qty
        if (Number(product.is_unlimited_stock)) {
            if (productQty < Number(product?.total_allowed_quantity)) {
                addToCart(product.id, selectedVariant?.id, cart?.cartProducts?.find(prdct => prdct?.product_variant_id == selectedVariant?.id)?.qty + 1);
            } else {
                toast.error('Apologies, maximum product quantity limit reached!');
            }
        } else {
            if (productQty >= Number(selectedVariant.stock)) {
                toast.error(t("out_of_stock_message"));
            } else if (Number(productQty) >= Number(product.total_allowed_quantity)) {
                toast.error('Apologies, maximum product quantity limit reached');
            } else {
                addToCart(product.id, selectedVariant?.id, cart?.cartProducts?.find(prdct => prdct?.product_variant_id == selectedVariant?.id)?.qty + 1);
            }
        }
    };

    const handleAddNewProductGuest = (productQuantity, product) => {
        const productQty = productQuantity?.find(prdct => prdct?.product_id == product?.id)?.qty
        if (selectedVariant?.is_unlimited_stock == 0 && selectedVariant?.stock == 0) {
            toast.error(t("out_of_stock_message"));
        } else if (Number(productQty || 0) < Number(product.total_allowed_quantity)) {
            AddToGuestCart(product, product.id, selectedVariant?.id, 1, 0, "add");
        } else {
            toast.error(t("out_of_stock_message"));
        }
    };

    const handleValidateAddExistingGuestProduct = (productQuantity, product, quantity) => {
        const productQty = productQuantity?.find(prdct => prdct?.product_id == product?.id)?.qty;

        if (Number(product.is_unlimited_stock !== 0)) {
            if (productQty >= Number(product?.total_allowed_quantity)) {
                toast.error('Apologies, maximum product quantity limit reached');
            } else {
                AddToGuestCart(product, product?.id, selectedVariant?.id, quantity, 1, "add");
            }
        } else {
            if (productQty >= Number(selectedVariant?.stock)) {
                toast.error('Oops, Limited Stock Available');
            } else if (productQty >= Number(product?.total_allowed_quantity)) {
                toast.error('Apologies, maximum cart quantity limit reached');
            } else {
                AddToGuestCart(product, product?.id, selectedVariant?.id, quantity, 1, "add");
            }
        }
    };

    // Quantity controls
    const handleQuantityDecrease = () => {
        if (cart?.isGuest) {
            AddToGuestCart(
                product,
                product?.id,
                selectedVariant?.id,
                cart?.guestCart?.find((prdct) => prdct?.product_variant_id == selectedVariant?.id)?.qty - 1,
                1,
                "remove"
            );
        } else {
            if (cart?.cartProducts?.find((prdct) => prdct?.product_variant_id == selectedVariant?.id).qty == 1) {
                removeFromCart(product?.id, selectedVariant?.id)
            } else {
                addToCart(product.id, selectedVariant.id, cart?.cartProducts?.find(prdct => prdct?.product_variant_id == selectedVariant?.id)?.qty - 1);
            }
        }
    }

    const handleQuantityIncrease = () => {
        if (!cart) return;
        
        if (cart.isGuest) {
            const guestCart = cart.guestCart || [];
            const productQuantity = getProductQuantities(guestCart);
            const existingProduct = guestCart.find(prdct => 
                prdct?.product_id === product?.id && 
                prdct?.product_variant_id === selectedVariant?.id
            );
            const newQty = (existingProduct?.qty || 0) + 1;
            
            handleValidateAddExistingGuestProduct(
                productQuantity,
                product,
                newQty
            );
        } else {
            const cartProducts = cart.cartProducts || [];
            const quantity = getProductQuantities(cartProducts);
            handleValidateAddExistingProduct(quantity, product);
        }
    }

    const handleIntialAddToCart = () => {
        if (!cart) return;
        
        if (cart.isGuest) {
            const cartProducts = cart.cartProducts || [];
            const quantity = getProductQuantities(cartProducts);
            handleAddNewProductGuest(quantity, product);
        } else {
            const cartProducts = cart.cartProducts || [];
            const quantity = getProductQuantities(cartProducts);
            handleValidateAddNewProduct(quantity, product);
        }
    }

    // Variant selection handlers
    const handleColorSelect = (color) => {
        setSelectedColor(color);
    };

    const handleSizeSelect = (size) => {
        setSelectedSize(size);
    };

    const handleVariantSelect = (variant) => {
        setSelectedVariant(variant);
        
        // Reset color and size for new variant
        if (isClothingProduct(variant)) {
            const colors = getVariantColors(variant);
            const sizes = getVariantSizes(variant);
            
            if (colors.length > 0) {
                setSelectedColor(colors[0]);
            } else {
                setSelectedColor(null);
            }
            
            if (sizes.length > 0) {
                setSelectedSize(sizes[0]);
            } else {
                setSelectedSize(null);
            }
        } else {
            setSelectedColor(null);
            setSelectedSize(null);
        }
    };

    const handleVariantModal = (product) => {
        if (product?.variants?.length > 1) {
            setShowVariantModal(true)
        }
    }

    // Computed values
    const isProductAvailabel = (
        (product?.variants?.length <= 1 && 
         product?.variants?.[0]?.is_unlimited_stock === 0 && 
         product?.variants?.[0]?.stock === 0) || 
        (selectedVariant?.stock === 0 && 
         selectedVariant?.is_unlimited_stock === 0) || 
        (product?.variants?.length <= 1 && 
         product?.variants?.[0]?.status === 0)
    );

    // تعديل البحث عن المنتج في السلة ليشمل اللون والمقاس
    const isProductAlreadyAdded = (
        (cart?.isGuest === false && 
            cart?.cartProducts?.find((prdct) => 
                prdct?.product_variant_id === selectedVariant?.id &&
                prdct?.selected_color === selectedColor &&
                prdct?.selected_size === selectedSize
            )?.qty > 0) ||
        (cart?.isGuest === true && 
            cart?.guestCart?.find((prdct) => 
                prdct?.product_variant_id === selectedVariant?.id &&
                prdct?.selected_color === selectedColor &&
                prdct?.selected_size === selectedSize
            )?.qty > 0)
    );

    const addedQuantity = cart?.isGuest === false
        ? cart?.cartProducts?.find(prdct => 
            prdct?.product_variant_id === selectedVariant?.id &&
            prdct?.selected_color === selectedColor &&
            prdct?.selected_size === selectedSize
          )?.qty || 0
        : cart?.guestCart?.find(prdct => 
            prdct?.product_variant_id === selectedVariant?.id &&
            prdct?.selected_color === selectedColor &&
            prdct?.selected_size === selectedSize
          )?.qty || 0;

    // Check if product has multiple variants
    const hasMultipleVariants = product?.variants && product.variants.length > 1;

    return (
        <div className="product-card">
            {/* Product Image Section */}
            <div className="product-image">
                <Link to={`/product/${product.slug}`} className='image'>
                    {/* Free Quantity Badge - New Position */}
                    {hasFreeQuantityOffer(selectedVariant) && (
                        <div className="free-quantity-badge-new">
                            <div className="gift-icon-new"></div>
                            <span className="gift-text-new">{getFreeQuantityText(selectedVariant)} هدية مجانية</span>
                        </div>
                    )}
                    
                    <ImageWithPlaceholder
                        src={selectedVariant?.image_url || product.image_url}
                        alt={product.name}
                    />
                </Link>

                {/* Discount Badge */}
                {(() => {
                    const now = new Date();
                    const startDate = selectedVariant?.discount_start_date ? new Date(selectedVariant.discount_start_date) : null;
                    const endDate = selectedVariant?.discount_end_date ? new Date(selectedVariant.discount_end_date) : null;
                    const isDiscountActive = selectedVariant?.discounted_price && 
                                           selectedVariant?.discounted_price !== 0 && 
                                           selectedVariant?.discounted_price < selectedVariant?.price &&
                                           startDate && endDate &&
                                           now >= startDate && now <= endDate;

                    if (isDiscountActive) {
                        return (
                            <div className="product-discount-label">
                                {calculateDiscount(selectedVariant?.discounted_price, selectedVariant?.price).toFixed(0)}%
                            </div>
                        );
                    }
                    return null;
                })()}



                {/* Product Actions */}
                <ul className="product-links">
                    <li>
                        <button
                            type='button'
                            onClick={() => addToFavorite(product.id)}
                            className='favorite-button'
                            aria-label="Add to favorites"
                        >
                            {favoriteProducts?.favoriteIds?.includes(product.id) ? (
                                <BiSolidHeart />
                            ) : (
                                <BiHeart />
                            )}
                        </button>
                    </li>
                    <li>
                        <button
                            type='button'
                            onClick={() => setShowModal(true)}
                            className='quick-view-button'
                            aria-label="Quick view"
                        >
                            <LuEye />
                        </button>
                    </li>
                </ul>
            </div>

            {/* Product Content */}
            <div className="product-content">
                {/* Product Title */}
                <h3 className="title">
                    <Link to={`/product/${product.slug}`}>{product.name}</Link>
                </h3>

                {/* Rating */}
                <div className="rating">
                    <Rate
                        disabled
                        defaultValue={product.ratings}
                        character={({ index }) => {
                            return index < product.ratings ? (
                                <StarFilled className='filledStar' />
                            ) : (
                                <StarOutlined className='emptyStar' />
                            )
                        }}
                    />
                    <p>({product.number_of_ratings})</p>
                </div>

                {/* Price */}
                <div className="price">
                    {(() => {
                        const now = new Date();
                        const startDate = selectedVariant?.discount_start_date ? new Date(selectedVariant.discount_start_date) : null;
                        const endDate = selectedVariant?.discount_end_date ? new Date(selectedVariant.discount_end_date) : null;
                        const isDiscountActive = selectedVariant?.discounted_price && 
                                               selectedVariant?.discounted_price !== 0 && 
                                               selectedVariant?.discounted_price < selectedVariant?.price &&
                                               startDate && endDate &&
                                               now >= startDate && now <= endDate;

                        if (isDiscountActive) {
                            return (
                                <>
                                   <div className='price-saudi'>
                                     {setting.setting?.currency_symbol}
                                    {selectedVariant?.discounted_price}
                                   </div>
                                    <span className='price-saudi'>
                                        {setting.setting?.currency_symbol}
                                        {selectedVariant?.price}
                                    </span>
                                </>
                            );
                        } else {
                            return (
                                <>
                                   <div className='price-saudi'>
                                     {setting.setting?.currency_symbol}
                                    {selectedVariant?.price}
                                   </div>
                                </>
                            );
                        }
                    })()}
                </div>

                {/* Compact Variant Selection */}
                {hasMultipleVariants && (
                    <div className="variant-options">
                        <div className="variant-list">
                            {product.variants.slice(0, 4).map((variant) => (
                                <button
                                    key={variant.id}
                                    className={`variant-option ${selectedVariant?.id === variant.id ? 'selected' : ''}`}
                                    onClick={() => handleVariantSelect(variant)}
                                    title={getVariantDisplayName(variant)}
                                >
                                    {getVariantDisplayName(variant)}
                                </button>
                            ))}
                            {product.variants.length > 4 && (
                                <button
                                    className="variant-more-btn"
                                    onClick={() => setShowVariantModal(true)}
                                    title={`عرض ${product.variants.length - 4} متغير إضافي`}
                                >
                                    +{product.variants.length - 4}
                                </button>
                            )}
                        </div>
                    </div>
                )}

                {/* Clothing Variant Options (Colors & Sizes) */}
                {selectedVariant && isClothingProduct(selectedVariant) && (
                    <div className="clothing-variant-options">
                        <div className="variant-row">
                            {/* Color Options */}
                            {getVariantColors(selectedVariant).length > 0 && (
                                <div className="variant-column">
                                    <div className="color-options">
                                        {getVariantColors(selectedVariant).slice(0, 4).map((color, index) => (
                                            <button
                                                key={index}
                                                className={`color-swatch ${selectedColor === color ? 'selected' : ''}`}
                                                style={{ backgroundColor: color }}
                                                onClick={() => handleColorSelect(color)}
                                                title={color}
                                                aria-label={`Select color ${color}`}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Size Options */}
                            {getVariantSizes(selectedVariant).length > 0 && (
                                <div className="variant-column">
                                    <div className="size-options">
                                        {getVariantSizes(selectedVariant).slice(0, 3).map((size, index) => (
                                            <button
                                                key={index}
                                                className={`size-button ${selectedSize === size ? 'selected' : ''}`}
                                                onClick={() => handleSizeSelect(size)}
                                            >
                                                {size}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Compact Add to Cart Section */}
                <div className="product-action-buttons">
                    {selectedVariant?.status === false || selectedVariant?.stock === 0 ? (
                        <div className="product-out-of-stock-overlay">
                            {t("out_of_stock_message")}
                        </div>
                    ) : isProductAlreadyAdded ? (
                        <div className="quantity-controls">
                            <button 
                                onClick={handleQuantityDecrease}
                                aria-label="Decrease quantity"
                            >
                                <FaMinus size={8} />
                            </button>
                            <span className="quantity-display">
                                {addedQuantity}
                            </span>
                            <button 
                                onClick={handleQuantityIncrease}
                                aria-label="Increase quantity"
                            >
                                <FaPlus size={8} />
                            </button>
                        </div>
                    ) : (
                        <button
                            className="add-to-cart-overlay"
                            onClick={handleIntialAddToCart}
                            disabled={!selectedVariant?.status || selectedVariant?.stock === 0}
                            aria-label="Add to cart"
                        >
                            <FaShoppingCart size={30} />
                        </button>
                    )}
                </div>

                {/* Discount Timer */}
                {discountCountdown && (
                    <div className="discount-timer">
                        {discountCountdown}
                    </div>
                )}
            </div>

            {/* Modals */}
            <QuickViewModal 
                show={showModal}
                onHide={() => setShowModal(false)}
                product={product} 
            />
        </div>
    );
}

export default ProductCard